import { ALL_THEMES } from 'lib/context/ThemeContext';
import { tv } from 'tailwind-variants';

const themeVariants = ALL_THEMES.reduce(
  (acc, curr) => ((acc[curr] = {}), acc),
  {} as Record<string, object>
);

export const tailwindVariants = tv({
  slots: {
    badgeBG: [
      'rounded-themes-spacing-radius-large-badge',
      'block',
      'font-bodySans-medium-semibold',
      'leading-bodySans-medium-semibold',
      'text-bodySans-medium-semibold',
      'px-[34px]',
      'py-[8px]',
      'break-normal',
      'text-center',
      'text-components-product-card-color-badge-text',
    ],
    badgeContainer: ['absolute', 'z-10'],
    buttonClasses: [
      'border-[3px]',
      'border-components-button-color-outline-white-outline-stroke',
      'group',
      'h-8',
      'mr-2',
      'rounded-themes-spacing-radius-large-button',
      'w-8',
      'active:bg-components-button-color-outline-white-pressed-bg',
      'active:border-components-button-color-outline-white-pressed-stroke',
      'disabled:bg-transparent',
      'disabled:border-components-button-color-outline-white-disabled-stroke',
      'disabled:border-components-button-color-outline-white-disabled-stroke',
      'disabled:pointer-events-none',
      'disabled:text-components-button-color-outline-white-disabled-text',
      'hover:bg-components-button-color-outline-white-hover-bg',
      'hover:border-components-button-color-outline-white-hover-stroke',
    ],
    cardContainer: [
      'flex',
      'flex-1',
      'flex-col',
      'gap-themes-general-card-spacing-large-eyebrow-margin-bottom',
    ],
    cardContentWrapper: [
      'flex',
      'flex-col',
      'flex-1',
      'justify-between',
      'py-components-product-card-spacing-large-padding-content-y',
      'px-components-product-card-spacing-large-padding-content-x',
      'gap-themes-general-card-spacing-large-body-margin-bottom',
    ],
    cardImgWrapper: [
      'aspect-card',
      'block',
      'flex',
      'justify-center',
      'relative',
      'w-full',
      'max-h-[330px]',
    ],
    cardLinkWrapper: ['block', 'w-full'],
    cardMedia: ['object-cover', 'min-h-full'],
    cardWrapper: [
      'bg-components-product-card-color-content-bg',
      'flex-col',
      'flex',
      'relative',
      'overflow-hidden',
      'rounded-themes-spacing-radius-large-card',
      'border-components-product-card-spacing-border-width',
      'border-components-product-card-color-border',
      'border-solid',
      'w-full',
    ],
    cta: ['w-full'],
    ctaWrapper: ['[&>a]:w-full'],
    labelBg: ['bg-components-product-card-color-content-bg'],
    productDescription: [
      'font-bodySans-small',
      'leading-bodySans-small',
      'text-bodySans-small',
      'line-clamp-2',
      'text-components-product-card-color-description',
      '[&>.richtext-wrapper>div>p]:p-0',
      '[&>.richtext-wrapper>div>p]:pt-[3px]',
      'md:leading-bodySans-medium',
      'md:font-bodySans-medium',
      'md:text-bodySans-medium',
      'mb-[-2px]',
      'pt-[4px]',
      'border-b-4',
      'pb-[5px]',
      'border-transparent',
      'max-h-[50px]',
    ],
    productInfoContainer: [
      'flex',
      'flex-1',
      'flex-col',
      'justify-between',
      'gap-themes-general-card-spacing-large-title-margin-bottom',
      '[&_.pr-snippet.pr-category-snippet]:!mb-0',
      '[&_.pr-snippet-stars.pr-snippet-stars-png]:!mb-0',
    ],
    productInfoTitleRatingsContainer: ['flex', 'flex-col'],
    ratingWrapper: ['mt-themes-general-card-spacing-large-title-margin-bottom'],
    productTitle: [
      'font-header-small-small',
      'leading-header-small-small',
      'text-header-small-small',
      'text-components-product-card-color-title',
      'md:font-header-large-xSmall',
      'md:leading-header-large-xSmall',
      'md:text-header-large-xSmall',
    ],
    rating: ['flex', 'flex-row', 'mb-themes-general-card-spacing-large-title-margin-bottom'],
    starIcon: ['fill-colors-accent-1-400'],
  },
  variants: {
    isBadgeFullWidth: {
      '1': { badgeBG: ['h-[39px]'], badgeContainer: ['w-full'] },
      undefined: {
        badgeContainer: ['top-0', 'left-1/2', 'transform', '-translate-x-1/2'],
      },
    },
    brand: {
      ...themeVariants,
      Pyrel: {
        badgeContainer: ['!top-0', '!left-0', 'translate-x-0'],
        badgeBG: ['!bg-bottom', '!pb-3'],
      },
      Pledge: {
        badgeContainer: ['!top-[24px]', '!left-0', 'translate-x-0'],
        badgeBG: ['!bg-bottom', '!py-[20px]', '!rounded-none'],
      },
      Lysoform: {
        badgeContainer: ['!top-[9.5px]', '!left-0', 'translate-x-0'],
        badgeBG: ['!bg-bottom', '!rounded-none'],
      },
      Baygon: {
        labelBg: [
          '!bg-components-label-color-on-white-background-bg-default',
          'hover:!bg-components-label-color-on-white-background-bg-hover',
        ],
        badgeContainer: ['top-[20px]', 'left-[24px]', 'translate-x-0'],
        badgeBG: ['!bg-center'],
      },
      Duck: {
        badgeContainer: ['!left-0', 'translate-x-0'],
        badgeBG: ['!bg-bottom'],
      },
      Ziploc: {
        badgeBG: ['h-[30px]', 'min-w-[130px]'],
      },
      MrMuscle: {
        badgeContainer: ['top-[20px]', 'left-[24px]', 'translate-x-0'],
        badgeBG: ['!bg-center'],
      },
      Drano: {
        badgeContainer: ['!top-[24px]', '!left-0', 'translate-x-0'],
        badgeBG: ['min-w-[108px] h-[35px] !pl-[10px] flex items-center'],
        ctaWrapper: ['[&>a]:!w-auto', 'md:px-1'],
      },
      Windex: {
        badgeContainer: ['!top-[24px]', '!left-0', 'translate-x-0'],
        badgeBG: [
          'min-w-[85px] min-h-[50px] !rounded-none aspect-[20/9] !pl-[10px] flex items-center',
        ],
      },
      ScrubbingBubbles: {
        badgeContainer: ['!top-[24px]', 'left-[24px]', 'translate-x-0'],
        badgeBG: ['!rounded-none', 'block', '!px-[24px]', 'pt-[12px]', 'pb-[12px]', 'text-center'],
      },
      Exposis: {
        badgeContainer: ['!top-[24px]', 'left-[24px]', 'translate-x-0'],
        badgeBG: ['!rounded-none', 'block', '!px-[24px]', 'pt-[12px]', 'pb-[12px]', 'text-center'],
      },
    },
  },
  compoundVariants: [
    {
      brand: 'Baygon',
      isBadgeFullWidth: '1',
      className: {
        badgeContainer: ['!top-0', '!left-0', 'translate-x-0'],
        badgeBG: ['!bg-center'],
      },
    },
    {
      brand: 'MrMuscle',
      isBadgeFullWidth: '1',
      className: {
        badgeContainer: ['!top-0', '!left-0', 'translate-x-0'],
        badgeBG: ['!bg-center'],
      },
    },
  ],
});
